/**
 * useFuelRates — shared hook for resolving current fuel prices.
 *
 * Priority order:
 *   1. Daily Sale Rate for the given date  (from /api/daily-sale-rates?date=...)
 *   2. Most recent Daily Sale Rate set across any date (from /api/daily-sale-rates)
 *   3. Most recent Nozzle Sale Entry price across any date (from /api/sale-entries)
 *   4. LFR/KL from Fuel Products master    (lfrn / lfr field)
 *   5. Most recent price from sales history (any historical sale)
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchJSON } from "@/lib/api";
import { fetchActiveFuelProducts, fuelProductLabel, parseLfrPrice, type FuelProductRow } from "@/lib/fuelProducts";

export interface FuelRateEntry {
  id: string;
  code: string;          // short name e.g. "MS", "HSD", "XP"
  productName: string;
  rate: number | undefined;   // resolved selling price
  source: "daily" | "daily_history" | "nozzle_history" | "lfr" | "history" | "none";
}

/** Fetch daily sale rates for a given date */
async function fetchDailyRates(date: string) {
  try {
    const res = await fetchJSON(`/api/daily-sale-rates?date=${date}`);
    const data = await res.json();
    if (data.ok || data.success) return (data.rows || []) as any[];
  } catch {/* ignore */}
  return [];
}

/** Fetch all daily sale rates for history fallback */
async function fetchAllDailyRates() {
  try {
    const res = await fetchJSON("/api/daily-sale-rates");
    const data = await res.json();
    if (data.ok || data.success) return (data.rows || []) as any[];
  } catch {/* ignore */}
  return [];
}

/** Fetch existing sale-entries (nozzle prices) for price fallback */
async function fetchSaleEntriesHistory() {
  try {
    const res = await fetchJSON("/api/sale-entries");
    const data = await res.json();
    if (data.ok || data.success) return (data.rows || []) as any[];
  } catch {/* ignore */}
  return [];
}

/** Fetch existing regular-sales entries for price fallback */
async function fetchRegularSalesHistory() {
  try {
    const res = await fetchJSON("/api/regular-sales");
    const data = await res.json();
    if (data.ok) return (data.rows || []) as any[];
  } catch {/* ignore */}
  return [];
}

/** Fetch existing credit-sales entries for price fallback */
async function fetchCreditSalesHistory() {
  try {
    const res = await fetchJSON("/api/credit-sales");
    const data = await res.json();
    if (data.ok || data.success) return (data.rows || []) as any[];
  } catch {/* ignore */}
  return [];
}

interface UseFuelRatesOptions {
  date: string;               // YYYY-MM-DD
  includeSalesHistory?: boolean; // whether to fall back to sales history
  onlySelectedDate?: boolean;   // if true, only resolve daily rate for the given date
}

export function useFuelRates({ date, includeSalesHistory = true, onlySelectedDate = false }: UseFuelRatesOptions) {

  const { data: fuelProducts = [] } = useQuery<FuelProductRow[]>({
    queryKey: ["/api/fuel-products"],
    queryFn: fetchActiveFuelProducts,
    staleTime: 5 * 60 * 1000,
  });

  const { data: dailyRates = [] } = useQuery({
    queryKey: ["/api/daily-sale-rates", date],
    queryFn: () => fetchDailyRates(date),
    enabled: !!date,
    staleTime: 0,
  });

  const { data: allDailyRates = [] } = useQuery({
    queryKey: ["/api/daily-sale-rates/all"],
    queryFn: fetchAllDailyRates,
    staleTime: 0,
  });

  const { data: saleEntriesHistory = [] } = useQuery({
    queryKey: ["/api/sale-entries/history"],
    queryFn: fetchSaleEntriesHistory,
    enabled: includeSalesHistory,
    staleTime: 0,
  });

  const { data: regularHistory = [] } = useQuery({
    queryKey: ["/api/regular-sales/history"],
    queryFn: fetchRegularSalesHistory,
    enabled: includeSalesHistory,
    staleTime: 0,
  });

  const { data: creditHistory = [] } = useQuery({
    queryKey: ["/api/credit-sales/history"],
    queryFn: fetchCreditSalesHistory,
    enabled: includeSalesHistory,
    staleTime: 0,
  });

  /** Map: productId → daily close/open rate for the specific date */
  const rateByProductId = useMemo(() => {
    const map: Record<number, number> = {};
    dailyRates.forEach((r: any) => {
      const id = String(r.fuel_product_id || r.fuelProductId || "").trim();
      const rate = r.close_rate ?? r.closeRate ?? r.open_rate ?? r.openRate;
      if (id && rate != null) map[id] = Number(rate);
    });
    return map;
  }, [dailyRates]);

  /** Map: productId → latest daily rate on or before the selected date */
  const latestDailyRateByProductId = useMemo(() => {
    const map: Record<string, number> = {};
    if (onlySelectedDate) return map;
    allDailyRates.forEach((r: any) => {
      const rDate = String(r.rate_date || r.rateDate || r.date || "").slice(0, 10);
      if (rDate && rDate <= date) {
        const id = String(r.fuel_product_id || r.fuelProductId || "").trim();
        const rate = r.close_rate ?? r.closeRate ?? r.open_rate ?? r.openRate;
        if (id && rate != null && !map[id]) map[id] = Number(rate);
      }
    });
    return map;
  }, [allDailyRates, date, onlySelectedDate]);

  /** Map: productId → latest nozzle price on or before the selected date */
  const priceFromSaleEntries = useMemo(() => {
    const map: Record<string, number> = {};
    saleEntriesHistory.forEach((r: any) => {
      const sDate = String(r.saleDate || r.sale_date || r.date || "").slice(0, 10);
      const isMatch = onlySelectedDate ? (sDate === date) : (sDate && sDate <= date);
      if (isMatch) {
        const id = String(r.fuel_product_id || r.fuelProductId || "").trim();
        const price = Number(r.price_per_unit ?? r.pricePerUnit ?? r.price ?? 0);
        if (id && price > 0 && !map[id]) map[id] = price;
      }
    });
    return map;
  }, [saleEntriesHistory, date, onlySelectedDate]);

  /** Map: productShortName(uppercase) → price from most recent sale on or before the selected date */
  const priceFromHistory = useMemo(() => {
    const map: Record<string, number> = {};
    // Merge regular + credit history, sort newest first
    const allRows = [
      ...regularHistory.map((r: any) => ({ prod: (r.product || "").toUpperCase().trim(), price: Number(r.price ?? r.price_per_unit ?? 0), date: String(r.date || r.sale_date || "") })),
      ...creditHistory.map((r: any) => ({ prod: (r.product_name || "").toUpperCase().trim(), price: Number(r.price_per_unit ?? 0), date: String(r.sale_date || "") })),
    ]
      .filter((r) => {
        const rDate = r.date.slice(0, 10);
        return onlySelectedDate ? (rDate === date) : (rDate <= date);
      })
      .sort((a, b) => b.date.localeCompare(a.date));

    allRows.forEach(({ prod, price }) => {
      if (prod && price > 0 && !map[prod]) map[prod] = price;
    });
    return map;
  }, [regularHistory, creditHistory, date, onlySelectedDate]);

  /** Resolved rates for all active fuel products */
  const rates = useMemo<FuelRateEntry[]>(() => {
    return fuelProducts.map((fp) => {
      const code = fuelProductLabel(fp).toUpperCase();
      const lfrPrice = parseLfrPrice(fp);

      // Priority 1: daily rate for the given date
      const dailyRateForDate = rateByProductId[String(fp.id)];
      if (dailyRateForDate != null) {
        return { id: fp.id, code, productName: fp.product_name || fp.productName || code, rate: dailyRateForDate, source: "daily" as const };
      }

      // If only selected date is requested, do not apply historical daily rate fallback
      if (!onlySelectedDate) {
        const historicalDailyRate = latestDailyRateByProductId[String(fp.id)];
        if (historicalDailyRate != null) {
          return { id: fp.id, code, productName: fp.product_name || fp.productName || code, rate: historicalDailyRate, source: "daily_history" as const };
        }
      }

      // Priority 3: most recent nozzle price from sale entries (on selected date if onlySelectedDate, on/before date otherwise)
      if (includeSalesHistory) {
        const saleEntryPrice = priceFromSaleEntries[String(fp.id)];
        if (saleEntryPrice != null) {
          return { id: fp.id, code, productName: fp.product_name || fp.productName || code, rate: saleEntryPrice, source: "nozzle_history" as const };
        }
      }

      // Priority 4: LFR/KL (ONLY if not onlySelectedDate)
      if (!onlySelectedDate && lfrPrice != null) {
        return { id: fp.id, code, productName: fp.product_name || fp.productName || code, rate: lfrPrice, source: "lfr" as const };
      }

      // Priority 5: sales history (on selected date if onlySelectedDate, on/before date otherwise)
      if (includeSalesHistory) {
        const histPrice = priceFromHistory[code];
        if (histPrice != null) {
          return { id: fp.id, code, productName: fp.product_name || fp.productName || code, rate: histPrice, source: "history" as const };
        }
      }

      return { id: fp.id, code, productName: fp.product_name || fp.productName || code, rate: undefined, source: "none" as const };
    }).sort((a, b) => a.code.localeCompare(b.code));
  }, [fuelProducts, rateByProductId, latestDailyRateByProductId, priceFromSaleEntries, priceFromHistory, includeSalesHistory, onlySelectedDate]);
  /** Quick lookup: get resolved price for a fuel product by id */
  const getRateById = useMemo(() => {
    const map = new Map(rates.map(r => [r.id, r.rate]));
    return (id: string): number | undefined => map.get(String(id));
  }, [rates]);

  /** Quick lookup: get resolved price for a fuel product by short name */
  const getRateByCode = useMemo(() => {
    const map = new Map(rates.map(r => [r.code, r.rate]));
    return (code: string): number | undefined => map.get(code.toUpperCase());
  }, [rates]);

  return { rates, rateByProductId, getRateById, getRateByCode, fuelProducts };
}

